import { ZoomIn } from "lucide-react";
import { FUNNEL, METRICS, SCENARIOS } from "../data/proposal";
import { SectionHead } from "./SectionHead";

const Funnel = ({ onZoom }) => (
  <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
    {FUNNEL.map((f) => (
      <button key={f.step} data-testid={`funnel-step-${f.step}`} onClick={() => onZoom(f)} className="group text-left">
        <div className="relative overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--warm)] p-3">
          <img src={f.img} alt={f.title} className="aspect-[1/2] w-full rounded-xl object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
          <span className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-[var(--orange)] opacity-0 transition-opacity group-hover:opacity-100"><ZoomIn size={18} /></span>
        </div>
        <p className="mt-3 text-sm font-black text-[var(--orange)]">STEP {f.step}</p>
        <p className="text-base font-bold text-[var(--ink)]">{f.title}</p>
        <p className="text-sm text-[var(--text)]">{f.text}</p>
      </button>
    ))}
  </div>
);

const Metrics = () => (
  <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    {METRICS.map((m, i) => (
      <div key={m.label} data-testid={`metric-${i}`} className={`rounded-2xl p-6 ${m.highlight ? "bg-[var(--orange)] text-white" : "border border-[var(--line)] bg-white"}`}>
        <p className={`text-sm ${m.highlight ? "text-white/90" : "text-[var(--text)]"}`}>{m.label}</p>
        <p className={`mt-2 text-4xl font-black ${m.highlight ? "" : "text-[var(--ink)]"}`}>約 {m.value}</p>
        <p className={`text-sm ${m.highlight ? "text-white/80" : "text-[var(--muted)]"}`}>{m.unit}</p>
      </div>
    ))}
  </div>
);

const Precision = () => (
  <div data-testid="precision-block" className="mt-6 grid gap-6 rounded-3xl bg-[var(--warm)] p-7 md:grid-cols-[1.2fr_1fr] md:items-center md:p-10">
    <div>
      <span className="result-tag">我們的曝光不一樣</span>
      <h3 className="mt-3 text-xl font-black text-[var(--ink)] md:text-2xl">精準曝光 ×20</h3>
      <p className="mt-1 font-bold text-[var(--ink)]">只投給「正在找地方旅遊訂房」的人</p>
      <p className="mt-2 text-[var(--text)]">
        一般的迷因、搞笑影片曝光，要帶來同樣多有興趣的客人，依我們過去經驗需要 <b className="text-[var(--orange)]">20 倍</b> 的曝光數。
      </p>
    </div>
    <div className="flex items-center gap-2 md:gap-4">
      <div className="flex-1 rounded-2xl bg-white p-4 md:p-5">
        <p className="text-sm text-[var(--text)]">人客多精準曝光</p>
        <p data-testid="precision-ours" className="mt-1 text-xl font-black text-[var(--orange)] whitespace-nowrap md:text-3xl">216,233</p>
      </div>
      <span className="text-lg font-black text-[var(--blue)]">≈</span>
      <div className="flex-1 rounded-2xl bg-white p-4 md:p-5">
        <p className="text-sm text-[var(--text)]">等同一般曝光</p>
        <p data-testid="precision-equivalent" className="mt-1 text-xl font-black text-[var(--blue)] whitespace-nowrap md:text-3xl">約 432 萬</p>
      </div>
    </div>
    <p className="text-sm text-[var(--muted)] md:col-span-2">換算：216,233 次 × 20 ≈ 4,324,660 次一般曝光（Mere Exposure）／2個月</p>
  </div>
);

const DirectBig = () => (
  <div data-testid="direct-big" className="mt-6 rounded-3xl bg-[var(--orange)] p-7 text-white md:p-10">
    <p className="text-sm font-black tracking-widest text-white/80">直接效應｜每月訂房</p>
    <p data-testid="direct-big-value" className="mt-2 text-4xl font-black md:text-6xl">約 69–185 間夜</p>
    <p className="mt-2 text-lg font-bold md:text-xl">每月營收多 NT$24–111 萬（與去年同期相比）</p>
    <div className="mt-6 grid gap-4 md:grid-cols-3">
      {SCENARIOS.map((s) => (
        <div key={s.name} data-testid={`direct-big-${s.name}`} className="rounded-2xl bg-white/15 p-5">
          <p className="text-sm font-black text-white/80">{s.name}｜成交率 {s.rate}</p>
          <p className="mt-1 text-3xl font-black md:text-4xl">{s.deals} 間夜</p>
          <p className="mt-1 text-sm text-white/90">{s.weekday} – {s.peak}</p>
        </div>
      ))}
    </div>
  </div>
);

const Forecast = () => (
  <div className="mt-16">
    <h3 className="text-xl font-black text-[var(--ink)]">成交試算</h3>
    <p className="mt-1 text-sm text-[var(--text)]">以「對名人堂有潛在興趣的人」2,315 人為基數，每筆成交以 1 間房 1 晚計。</p>
    <DirectBig />
    <div className="mt-6 overflow-x-auto rounded-2xl border border-[var(--line)]">
      <table data-testid="forecast-table" className="w-full min-w-[560px] whitespace-nowrap text-left">
        <thead className="bg-[var(--warm)] text-sm text-[var(--text)]">
          <tr><th className="p-4">情境</th><th className="p-4">成交率</th><th className="p-4">預估成交</th><th className="p-4">平日 NT$3,500</th><th className="p-4">旺日 NT$6,000</th></tr>
        </thead>
        <tbody>
          {SCENARIOS.map((s) => (
            <tr key={s.name} data-testid={`forecast-row-${s.name}`} className="border-t border-[var(--line)]">
              <td className="p-4 font-bold text-[var(--ink)]">{s.name}</td>
              <td className="p-4 text-[var(--text)]">{s.rate}</td>
              <td className="p-4 font-bold text-[var(--ink)]">約 {s.deals} 間夜</td>
              <td className="p-4 font-black text-[var(--orange)]">{s.weekday}</td>
              <td className="p-4 font-black text-[var(--orange)]">{s.peak}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-3 text-sm text-[var(--muted)]">實際營收會落在平日價和旺日價之間，看入住日期怎麼分布。</p>
  </div>
);

export const Results = ({ onZoom }) => (
  <section data-testid="results-section" className="mx-auto max-w-6xl px-5 py-16">
    <SectionHead no="02" title="客人會走的 4 步" sub="從廣告到訂房（點圖可放大）" />
    <Funnel onZoom={onZoom} />
    <Metrics />
    <Precision />
    <Forecast />
  </section>
);
